import React, { useState, useEffect, useRef } from 'react'
import {
  TextField,
  Button,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  useTheme,
  useMediaQuery
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import { makeStyles } from '@mui/styles'
import { Filter } from '../../../components/_WMS/FilterComponent'

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginBottom: theme.spacing(2)
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end', // Alineado a la derecha en desktop
    gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column' // Apilado en mobile
    }
  }
}))

interface DispatchOtNewSearchProps {
  onSearchClick: (filters: Filter[]) => void
  onClear: () => void
}

type SearchType = 'workOrderId' | 'pickingRouteId'

export const DispatchOtNewSearch: React.FC<DispatchOtNewSearchProps> = ({
  onSearchClick,
  onClear
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const [searchType, setSearchType] = useState<SearchType>('workOrderId')
  const [searchValue, setSearchValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSearchTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchType(event.target.value as SearchType)
    setSearchValue('')
  }

  // Auto focus en el input cuando se monta el componente o cambia el tipo de búsqueda
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [searchType])

  const handleSearchValueChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value.replace(/[^0-9]/g, '')
    setSearchValue(value)
  }

  const handleSearch = () => {
    if (!searchValue) {
      onSearchClick([])
      return
    }

    const filter: Filter = {
      key: searchType,
      value: searchValue,
      label: searchType === 'workOrderId' ? 'Work Order' : 'Ruta de Picking',
      displayValue: searchValue
    }
    onSearchClick([filter])
  }

  const handleClear = () => {
    setSearchValue('')
    onClear()
  }

  return (
    <Grid container spacing={2} alignItems='center'>
      <Grid item xs={12} md={4}>
        <FormControl component='fieldset' className={classes.formControl}>
          <FormLabel component='legend'>Buscar por:</FormLabel>
          <RadioGroup
            row
            aria-label='search-type'
            name='search-type'
            value={searchType}
            onChange={handleSearchTypeChange}
          >
            <FormControlLabel
              value='workOrderId'
              control={<Radio color='primary' />}
              label='OT'
            />
            <FormControlLabel
              value='pickingRouteId'
              control={<Radio color='primary' />}
              label='Ruta de Picking'
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          variant='outlined'
          size='small'
          label={`ID de ${
            searchType === 'workOrderId' ? 'Orden de Trabajo' : 'Ruta de Picking'
          }`}
          value={searchValue}
          onChange={handleSearchValueChange}
          placeholder='Ingrese el ID'
          inputRef={inputRef}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <div className={classes.buttonContainer}>
          <Button
            variant='contained'
            onClick={handleSearch}
            disabled={!searchValue}
            fullWidth={isMobile}
            startIcon={<SearchIcon />}
            sx={{
              textTransform: 'uppercase',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              height: '40px',
              borderRadius: '12px',
              backgroundColor: '#004bb1',
              color: 'white',
              '&:hover': { backgroundColor: '#003a88' },
              '&.Mui-disabled': {
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            Buscar
          </Button>
          <Button
            variant='outlined'
            onClick={handleClear}
            fullWidth={isMobile}
            startIcon={<CleaningServicesIcon />}
            sx={{
              textTransform: 'uppercase',
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              height: '40px',
              borderRadius: '12px',
              borderColor: '#004bb1',
              color: '#004bb1',
              '&:hover': {
                backgroundColor: '#004bb1',
                color: 'white',
                borderColor: '#004bb1'
              }
            }}
          >
            Limpiar
          </Button>
        </div>
      </Grid>
    </Grid>
  )
}